/**
 * Hub → WTC Hub extension unlock hand-off (vault Phase 5b side of the contract
 * in `extensions/wtc-hub/VAULT.md`, Phase 6):
 *
 *   chrome.runtime.sendMessage(EXTENSION_ID, { type: "WTC_VAULT_UNLOCK", unlockToken, expiresAt })
 *   chrome.runtime.sendMessage(EXTENSION_ID, { type: "WTC_VAULT_LOCK" })
 *
 * The extension accepts these only when Chrome reports the sender origin as a
 * Hub origin (`externally_connectable`), and keeps the token in
 * `chrome.storage.session`. Hub's server never sees it.
 *
 * Inert unless `NEXT_PUBLIC_WTC_HUB_EXTENSION_ID` is set at build time AND the
 * page is running in a Chromium browser that exposes `chrome.runtime`. Failures
 * (extension not installed, older build) are silent by design: the vault works
 * without the extension, and nothing here may log the token.
 *
 * The message shapes are the shared protocol in
 * `@west-tennessee-consulting/vault-client/unlock-protocol` (the extension
 * validates them with the same schema).
 */
import { SettingsSyncReply, type SettingsSyncMessage, type UnlockMessage } from "@west-tennessee-consulting/vault-client/unlock-protocol";
import type { DeviceSettings } from "@west-tennessee-consulting/vault-client/device-settings";

interface ChromeRuntimeLike {
  sendMessage(extensionId: string, message: unknown, callback?: (response: unknown) => void): void;
  readonly lastError?: { readonly message?: string } | undefined;
}

function isRuntime(value: unknown): value is ChromeRuntimeLike {
  return typeof value === "object" && value !== null && "sendMessage" in value && typeof value.sendMessage === "function";
}

function runtime(): ChromeRuntimeLike | null {
  if (typeof window === "undefined") return null;
  const chromeObj: unknown = Reflect.get(window, "chrome");
  if (typeof chromeObj !== "object" || chromeObj === null) return null;
  const rt: unknown = Reflect.get(chromeObj, "runtime");
  return isRuntime(rt) ? rt : null;
}

function extensionId(): string | null {
  const id = process.env.NEXT_PUBLIC_WTC_HUB_EXTENSION_ID;
  return id && /^[a-p]{32}$/.test(id) ? id : null;
}

/** Send and wait for the extension's reply (null when absent / no reply within `ms`). */
function request(message: Readonly<Record<string, unknown>>, ms: number): Promise<unknown> {
  const id = extensionId();
  const rt = runtime();
  if (!id || !rt) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), ms);
    try {
      rt.sendMessage(id, message, (response) => {
        void rt.lastError;
        window.clearTimeout(timer);
        resolve(response ?? null);
      });
    } catch {
      window.clearTimeout(timer);
      resolve(null); // not installed / not connectable — nothing to step aside
    }
  });
}

function isBusy(response: unknown): boolean {
  return typeof response === "object" && response !== null && "ok" in response && response.ok === false && "reason" in response && response.reason === "passkey_request_in_progress";
}

/**
 * VAULT.md "Hub contract" 1: while the extension is Chrome's WebAuthn proxy, Hub's own
 * unlock ceremony would be routed to it and fail. Ask it to step aside (BEGIN), run the
 * ceremony, then always END. A missing extension is ignored.
 */
export async function withExtensionSteppedAside<T>(ceremony: () => Promise<T>): Promise<T> {
  let reply = await request({ type: "WTC_VAULT_WEBAUTHN_BEGIN" }, 3000);
  for (let attempt = 0; attempt < 10 && isBusy(reply); attempt++) {
    await new Promise((r) => window.setTimeout(r, 1000));
    reply = await request({ type: "WTC_VAULT_WEBAUTHN_BEGIN" }, 3000);
  }
  try {
    return await ceremony();
  } finally {
    if (reply !== null) void request({ type: "WTC_VAULT_WEBAUTHN_END" }, 3000);
  }
}

function send(message: Readonly<Record<string, string>>): void {
  const id = extensionId();
  const rt = runtime();
  if (!id || !rt) return;
  try {
    // The callback reads lastError so Chrome does not report "Unchecked runtime.lastError".
    rt.sendMessage(id, message, () => {
      void rt.lastError;
    });
  } catch {
    // Extension absent or not connectable from this origin — the vault works without it.
  }
}

/** True when this build is configured to hand the unlock to the extension. */
export function extensionHandoffConfigured(): boolean {
  return extensionId() !== null;
}

export function handOffUnlockToExtension(unlockToken: string, expiresAtIso: string): void {
  const message: UnlockMessage = { type: "WTC_VAULT_UNLOCK", unlockToken, expiresAt: expiresAtIso };
  send(message);
}

/**
 * D-22: Hub web and the extension in this browser are one device. Send Hub's device settings
 * ("Lock after", clipboard clear - non-secret); the extension keeps the newer record and answers
 * with it. Null when there is no extension (or an older build that doesn't know the message).
 */
export async function syncDeviceSettingsWithExtension(settings: DeviceSettings): Promise<DeviceSettings | null> {
  const message: SettingsSyncMessage = { type: "WTC_VAULT_SETTINGS_SYNC", settings };
  const reply = SettingsSyncReply.safeParse(await request(message, 1500));
  return reply.success ? reply.data.settings : null;
}

export function tellExtensionLocked(): void {
  const message: UnlockMessage = { type: "WTC_VAULT_LOCK" };
  send(message);
}
